// Client/src/components/Portal.tsx
import { Children, ReactNode, isValidElement, useMemo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { UiComponentKind, useUiComponent } from '../utils/uiComponentTracker';

interface PortalProps {
  children: ReactNode;
  wrapperId?: string;
  label?: string;
  track?: boolean;
  kind?: UiComponentKind;
}

let activeModalPortalCount = 0;

const BODY_LOCK_STYLE_KEYS = [
  'overflow',
  'position',
  'top',
  'left',
  'right',
  'width',
] as const;

const HTML_LOCK_STYLE_KEYS = ['overflow'] as const;

const getDatasetKey = (key: string) => `portalPrev${key.charAt(0).toUpperCase()}${key.slice(1)}`;

const lockModalScroll = () => {
  const { body, documentElement } = document;

  if (activeModalPortalCount === 0) {
    const scrollY = window.scrollY || window.pageYOffset || 0;

    BODY_LOCK_STYLE_KEYS.forEach((key) => {
      body.dataset[getDatasetKey(key)] = body.style[key] || '';
    });
    HTML_LOCK_STYLE_KEYS.forEach((key) => {
      documentElement.dataset[getDatasetKey(key)] = documentElement.style[key] || '';
    });

    body.dataset.portalScrollY = String(scrollY);

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    documentElement.style.overflow = 'hidden';
    body.classList.add('portal-modal-open');
    documentElement.classList.add('portal-modal-open');
  }

  activeModalPortalCount += 1;
};

const unlockModalScroll = () => {
  const { body, documentElement } = document;

  activeModalPortalCount = Math.max(0, activeModalPortalCount - 1);

  if (activeModalPortalCount > 0) {
    return;
  }

  const scrollY = Number(body.dataset.portalScrollY || '0');

  BODY_LOCK_STYLE_KEYS.forEach((key) => {
    const datasetKey = getDatasetKey(key);
    body.style[key] = body.dataset[datasetKey] || '';
    delete body.dataset[datasetKey];
  });
  HTML_LOCK_STYLE_KEYS.forEach((key) => {
    const datasetKey = getDatasetKey(key);
    documentElement.style[key] = documentElement.dataset[datasetKey] || '';
    delete documentElement.dataset[datasetKey];
  });

  delete body.dataset.portalScrollY;
  body.classList.remove('portal-modal-open');
  documentElement.classList.remove('portal-modal-open');

  window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
};

/**
 * Portal-Komponente zum Rendern von Inhalten außerhalb der React-Hierarchie
 * Standardmäßig wird in #modal-root gerendert, das über allem liegt
 */
const inferLabelFromChildren = (children: ReactNode) => {
  const childList = Children.toArray(children);
  for (const child of childList) {
    if (!isValidElement(child)) continue;
    const childType = child.type;
    if (typeof childType === 'string') {
      continue;
    }
    if (typeof childType === 'function' || typeof childType === 'object') {
      const namedType = childType as { displayName?: string; name?: string };
      return namedType.displayName || namedType.name || null;
    }
  }
  return null;
};

export default function Portal({
  children,
  wrapperId = 'modal-root',
  label,
  track = true,
  kind = 'modal',
}: PortalProps) {
  const [wrapperElement, setWrapperElement] = useState<HTMLElement | null>(null);
  const resolvedLabel = useMemo(() => {
    if (label && label.trim()) return label.trim();
    const inferred = inferLabelFromChildren(children);
    if (inferred) return inferred;
    return wrapperId ? `Portal(${wrapperId})` : 'Portal';
  }, [label, children, wrapperId]);

  useUiComponent(track ? resolvedLabel : null, kind);

  useEffect(() => {
    let element = document.getElementById(wrapperId);
    let systemCreated = false;

    // Wenn das Element nicht existiert, erstelle es
    if (!element) {
      systemCreated = true;
      element = document.createElement('div');
      element.setAttribute('id', wrapperId);
      document.body.appendChild(element);
    }

    setWrapperElement(element);

    if (kind === 'modal') {
      lockModalScroll();
    }

    // Cleanup: Entferne das Element nur, wenn wir es erstellt haben
    return () => {
      if (kind === 'modal') {
        unlockModalScroll();
      }
      if (systemCreated && element?.parentNode) {
        element.parentNode.removeChild(element);
      }
    };
  }, [wrapperId, kind]);

  // Warte bis das Element bereit ist
  if (wrapperElement === null) return null;

  return createPortal(children, wrapperElement);
}
